import {
  ReactNode,
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { ILessons } from '@dtos/Course'
import { useGetLessonExtraContents } from '@services/api/graphql/lessonExtraContents'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface ILessonExtraContentsProviderProps {
  lessonsExtraContents?: ILessons
  isLoading: boolean
  error?: Error
  onLoadMore?: (offset: number) => Promise<{ lessons: ILessons | null }>
  resetLessonExtraContents?: () => void
}

export const LessonExtraContentsContext =
  createContext<ILessonExtraContentsProviderProps>(
    {} as ILessonExtraContentsProviderProps,
  )

export function LessonExtraContentsProvider({
  children,
}: {
  children: ReactNode
}) {
  const [lessonExtraContentsState, setLessonExtraContentsState] = useState<
    ILessons | undefined | null
  >()
  const showApolloErrorToast = useApolloErrorToast()
  const { courseId } = useCourseRouteParams()
  const { getMoreLessonExtraContents, loading, error } =
    useGetLessonExtraContents({
      id: `${courseId}`,
      offset: 0,
      limit: 5,
    })

  const resetLessonExtraContents = useCallback(() => {
    setLessonExtraContentsState(undefined)
  }, [])

  useEffect(() => {
    if (lessonExtraContentsState) return
    const fetchData = async () => {
      const { data } = await getMoreLessonExtraContents({
        variables: {
          id: courseId,
          offset: 0,
          limit: 5,
        },
      })
      if (data?.course.lessons) {
        setLessonExtraContentsState(data?.course.lessons)
      }
    }
    fetchData()
  }, [lessonExtraContentsState, getMoreLessonExtraContents, courseId])

  useEffect(() => {
    if (error && error.message !== 'Empty page!') {
      showApolloErrorToast(error, 'apolloErrors.lessonExtraContents.title')
    }
  }, [error, showApolloErrorToast])

  const onLoadMore = useCallback(
    async (offset: number) => {
      try {
        const { data } = await getMoreLessonExtraContents({
          variables: {
            id: courseId,
            offset,
            limit: 5,
          },
        })
        return data?.course
      } catch (error) {
        showApolloErrorToast(error, 'apolloErrors.lessonExtraContents.title')
      }
    },
    [getMoreLessonExtraContents, courseId, showApolloErrorToast],
  )

  const isLoading = loading || !lessonExtraContentsState

  const contextValue = useMemo(
    () => ({
      lessonsExtraContents: lessonExtraContentsState
        ? { ...lessonExtraContentsState }
        : undefined,
      isLoading,
      error,
      resetLessonExtraContents,
      onLoadMore: isLoading ? undefined : onLoadMore,
    }),
    [
      lessonExtraContentsState,
      isLoading,
      error,
      resetLessonExtraContents,
      onLoadMore,
    ],
  )

  return (
    <LessonExtraContentsContext.Provider value={contextValue}>
      {children}
    </LessonExtraContentsContext.Provider>
  )
}
